// components/ComprasOrderPDF.tsx
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { ICompra } from '../../types/compras.types';

// Estilos para el PDF
// Primero, mejoremos los estilos
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 30,
        borderBottom: 2,
        paddingBottom: 10,
    },
    title: {
        fontSize: 20,
        textAlign: 'center',
        marginBottom: 10,
        fontWeight: 'bold',
        color: '#1565C0', // Azul corporativo
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 10,
        backgroundColor: '#f5f5f5',
        padding: 5,
        color: '#2E7D32', // Verde para títulos de sección
    },
    infoSection: {
        marginBottom: 20,
        padding: 10,
        backgroundColor: '#FFFFFF',
        borderRadius: 5,
    },
    row: {
        flexDirection: 'row',
        marginBottom: 5,
        borderBottomWidth: 0.5,
        borderBottomColor: '#E0E0E0',
        paddingBottom: 2,
    },
    label: {
        width: '25%',
        fontWeight: 'bold',
        color: '#333333',
    },
    value: {
        width: '75%',
        color: '#666666',
    },
    table: {
        marginVertical: 15,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1565C0',
        padding: 8,
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
    },
    tableHeaderText: {
        color: '#FFFFFF',
        fontWeight: 'bold',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 8,
        backgroundColor: '#FFFFFF',
    },    
    col1: { width: '40%' },
    col2: { width: '15%', textAlign: 'center' },
    col3: { width: '20%', textAlign: 'right' },
    col4: { width: '25%', textAlign: 'right' },
    totals: {
        marginTop: 20,
        borderTopWidth: 2,
        borderTopColor: '#1565C0',
        paddingTop: 10,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 5,
    },
    totalLabel: {
        fontWeight: 'bold',
        marginRight: 20,
        color: '#1565C0',
    },
    totalValue: {
        fontWeight: 'bold',
        color: '#2E7D32',
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
    },
    signatures: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 60,
    },
    signature: {
        width: '45%',
        borderTopWidth: 1,
        borderTopColor: '#333333',
        paddingTop: 10,
        textAlign: 'center',
    },
});

interface CompraOrderPDFProps {
    compra: {
        OrdenCompra: string;
        FechaSolicitud: string;
        FechaEntrega: string;
        empresa: {
            Nombre: string;
            NIT: string;
            Direccion: string;
        };
        proveedor: {
            Nombre: string;
            NumeroIdentificacion: string;
            Direccion: string;
            Telefono: string;
            RepresentanteLegal: string;
        };
        articulosCompra: Array<{
            articulo: {
                Nombre: string;
                Descripcion: string;
            };
            CantidadComprada: number;
            PrecioCompra: number;
            Total: number;
        }>;
        estadoCompra: {
            Nombre: string;
        };
        usuario: {
            Usuario: string;
        };
    };
}

const CompraOrderPDF: React.FC<CompraOrderPDFProps> = ({ compra }) => {
    const total = compra.articulosCompra.reduce((sum, item) => sum + item.Total, 0);
    const formatCurrency = (value: number) =>
        new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' })
            .format(value)
            .replace('COP', '$');

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header con Logo */}
                <View style={styles.header} fixed>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View>
                            {/* Aquí iría el logo. Por ahora un placeholder */}
                            <View style={{ width: 100, height: 50, backgroundColor: '#1565C0' }}>
                                <Text style={{ color: 'white', textAlign: 'center', padding: 5 }}>LOGO</Text>
                            </View>
                        </View>
                        <View>
                            <Text style={styles.title}>ORDEN DE COMPRA #{compra.OrdenCompra}</Text>
                        </View>
                    </View>
                </View>

                {/* Información de la Empresa */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionTitle}>Información de la Empresa</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Empresa:</Text>
                        <Text style={styles.value}>{compra.empresa.Nombre}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>NIT:</Text>
                        <Text style={styles.value}>{compra.empresa.NIT}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Dirección:</Text>
                        <Text style={styles.value}>{compra.empresa.Direccion}</Text>
                    </View>
                </View>

                {/* Información del Proveedor */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionTitle}>Información del Proveedor</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Proveedor:</Text>
                        <Text style={styles.value}>{compra.proveedor.Nombre}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>NIT:</Text>
                        <Text style={styles.value}>{compra.proveedor.NumeroIdentificacion}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Dirección:</Text>
                        <Text style={styles.value}>{compra.proveedor.Direccion}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Teléfono:</Text>
                        <Text style={styles.value}>{compra.proveedor.Telefono}</Text>
                    </View>
                </View>

                {/* Detalles de la Orden */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionTitle}>Detalles de la Orden</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Fecha Solicitud:</Text>
                        <Text style={styles.value}>
                            {format(/^\d{4}-\d{2}-\d{2}$/.test(compra.FechaSolicitud) ? new Date(compra.FechaSolicitud + 'T12:00:00') : new Date(compra.FechaSolicitud), 'dd/MM/yyyy')}
                        </Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Fecha Entrega:</Text>
                        <Text style={styles.value}>
                            {format(/^\d{4}-\d{2}-\d{2}$/.test(compra.FechaEntrega) ? new Date(compra.FechaEntrega + 'T12:00:00') : new Date(compra.FechaEntrega), 'dd/MM/yyyy')}
                        </Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Estado:</Text>
                        <Text style={styles.value}>{compra.estadoCompra.Nombre}</Text>
                    </View>
                </View>

                {/* Tabla de Artículos */}
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableHeaderText, styles.col1]}>Artículo</Text>
                        <Text style={[styles.tableHeaderText, styles.col2]}>Cantidad</Text>
                        <Text style={[styles.tableHeaderText, styles.col3]}>Precio Unit.</Text>
                        <Text style={[styles.tableHeaderText, styles.col4]}>Total</Text>
                    </View>
                    {compra.articulosCompra.map((item, index) => (
                        <View
                            key={index}
                            style={[
                                styles.tableRow,
                                // Replace the conditional AND with a proper conditional style object
                                { backgroundColor: index % 2 === 1 ? '#F5F5F5' : '#FFFFFF' }
                            ]}
                        >
                            <Text style={styles.col1}>{item.articulo.Nombre}</Text>
                            <Text style={styles.col2}>{item.CantidadComprada}</Text>
                            <Text style={styles.col3}>{formatCurrency(item.PrecioCompra)}</Text>
                            <Text style={styles.col4}>{formatCurrency(item.Total)}</Text>
                        </View>
                    ))}
                </View>

                {/* Totales */}
                <View style={styles.totals}>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total:</Text>
                        <Text style={styles.totalValue}>{formatCurrency(total)}</Text>
                    </View>
                </View>

                {/* Footer y Firmas */}
                <View style={styles.footer} fixed>
                    <View style={styles.signatures}>
                        <View style={styles.signature}>
                            <Text>Elaborado por: _________________</Text>
                        </View>
                        <View style={styles.signature}>
                            <Text>Autorizado por: _________________</Text>
                        </View>
                    </View>
                    {/* Número de página */}
                    <Text style={{ textAlign: 'right', marginTop: 10, fontSize: 8, color: '#666' }} render={({ pageNumber, totalPages }) =>
                        `Página ${pageNumber} de ${totalPages}`
                    } />
                </View>
            </Page>
        </Document>
    );
};

export default CompraOrderPDF;